/**
 * Image Optimization Utility using Pica
 * 
 * Provides functions to resize images while preserving transparency (PNG).
 * Used for:
 * - Optimizing uploaded images to max 4K resolution
 * - Generating preview thumbnails (max 200px)
 */

import Pica from 'pica'
import { logger } from '@/lib/logger'

const pica = new Pica()

// Maximum dimensions
const MAX_FULL_SIZE = 3840 // 4K resolution
const MAX_PREVIEW_SIZE = 200 // Preview thumbnail size

interface ResizeResult {
  blob: Blob
  width: number
  height: number
}

/**
 * Load an image from a File or Blob into an HTMLImageElement
 */
async function loadImage(source: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(img.src)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('Failed to load image'))
    }
    img.src = URL.createObjectURL(source)
  })
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxSize: number
): { width: number; height: number; needsResize: boolean } {
  const needsResize = originalWidth > maxSize || originalHeight > maxSize
  
  if (!needsResize) {
    return { width: originalWidth, height: originalHeight, needsResize: false }
  }
  
  const aspectRatio = originalWidth / originalHeight
  
  let width: number
  let height: number
  
  if (originalWidth > originalHeight) {
    width = maxSize
    height = Math.round(maxSize / aspectRatio)
  } else {
    height = maxSize
    width = Math.round(maxSize * aspectRatio)
  }
  
  return { width, height, needsResize: true }
}

/**
 * Resize an image using Pica
 * Preserves transparency for PNG images
 */
async function resizeImage(
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  mimeType: string
): Promise<ResizeResult> {
  // Create source canvas
  const sourceCanvas = document.createElement('canvas')
  sourceCanvas.width = img.naturalWidth
  sourceCanvas.height = img.naturalHeight
  const sourceCtx = sourceCanvas.getContext('2d')!
  sourceCtx.drawImage(img, 0, 0)
  
  // Create destination canvas
  const destCanvas = document.createElement('canvas')
  destCanvas.width = targetWidth
  destCanvas.height = targetHeight
  
  // Use pica for high-quality resize
  // Note: Pica automatically preserves alpha channel when using canvas
  await pica.resize(sourceCanvas, destCanvas, {
    quality: 3, // High quality (1-3, 3 is best)
  })
  
  // Convert to blob
  // Use PNG for transparency, WebP as fallback (also supports transparency)
  const outputMimeType = mimeType === 'image/png' ? 'image/png' : 
                         mimeType === 'image/webp' ? 'image/webp' : 
                         'image/png' // Default to PNG to preserve any potential transparency
  
  const blob = await pica.toBlob(destCanvas, outputMimeType, 0.9)
  
  return {
    blob,
    width: targetWidth,
    height: targetHeight
  }
}

/**
 * Optimize an image for storage (max 4K resolution)
 * Returns the optimized blob and dimensions
 */
export async function optimizeImageForStorage(
  file: File | Blob,
  mimeType?: string
): Promise<ResizeResult & { wasResized: boolean }> {
  const type = mimeType || (file instanceof File ? file.type : 'image/png')
  
  try {
    const img = await loadImage(file)
    const { width, height, needsResize } = calculateDimensions(
      img.naturalWidth,
      img.naturalHeight,
      MAX_FULL_SIZE
    )
    
    if (!needsResize) {
      // Image is already small enough, return as-is
      return {
        blob: file,
        width: img.naturalWidth,
        height: img.naturalHeight,
        wasResized: false
      }
    }
    
    logger.info(`[ImageOptimizer] Resizing image from ${img.naturalWidth}x${img.naturalHeight} to ${width}x${height}`)
    
    const result = await resizeImage(img, width, height, type)
    return {
      ...result,
      wasResized: true
    }
  } catch (error) {
    logger.error('[ImageOptimizer] Failed to optimize image:', error)
    throw error
  }
}

/**
 * Generate a preview thumbnail (max 200px)
 * Returns the preview blob and dimensions
 */
export async function generatePreviewImage(
  file: File | Blob,
  mimeType?: string
): Promise<ResizeResult> {
  const type = mimeType || (file instanceof File ? file.type : 'image/png')
  
  try {
    const img = await loadImage(file)
    const { width, height, needsResize } = calculateDimensions(
      img.naturalWidth,
      img.naturalHeight,
      MAX_PREVIEW_SIZE
    )
    
    if (!needsResize) {
      // Image is already small enough for preview
      return {
        blob: file,
        width: img.naturalWidth,
        height: img.naturalHeight
      }
    }
    
    logger.info(`[ImageOptimizer] Generating preview from ${img.naturalWidth}x${img.naturalHeight} to ${width}x${height}`)
    
    return await resizeImage(img, width, height, type)
  } catch (error) {
    logger.error('[ImageOptimizer] Failed to generate preview:', error)
    throw error
  }
}

/**
 * Generate a preview from a URL (for existing images without previews)
 */
export async function generatePreviewFromUrl(
  url: string,
  mimeType: string = 'image/png'
): Promise<ResizeResult> {
  try {
    // Fetch the image
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`)
    }
    
    const blob = await response.blob()
    return await generatePreviewImage(blob, mimeType)
  } catch (error) {
    logger.error('[ImageOptimizer] Failed to generate preview from URL:', error)
    throw error
  }
}

/**
 * Process an image for upload: optimize full size and generate preview
 */
export async function processImageForUpload(
  file: File
): Promise<{
  optimizedBlob: Blob
  previewBlob: Blob
  width: number
  height: number
  previewWidth: number
  previewHeight: number
  wasResized: boolean
}> {
  // Optimize the full image
  const optimized = await optimizeImageForStorage(file, file.type)
  
  // Generate preview from the optimized image (faster than from original)
  const preview = await generatePreviewImage(optimized.blob, file.type)
  
  return {
    optimizedBlob: optimized.blob,
    previewBlob: preview.blob,
    width: optimized.width,
    height: optimized.height,
    previewWidth: preview.width,
    previewHeight: preview.height,
    wasResized: optimized.wasResized
  }
}
